/**
 * Lake Bennett Estate task-type dictionary.
 * Derived from the Operations & Maintenance Manual:
 * https://docs.lakebennettestate.com/
 *
 * Selecting a type in the create/edit form populates title, description,
 * location, ideal outcome, category, and priority by default.
 */

const DOC = 'https://docs.lakebennettestate.com';

const type = (entry) => ({
  priority: 'medium',
  location: '',
  ideal_outcome: '',
  category: '',
  docsUrl: '',
  ...entry,
});

export const TASK_TYPES = {
  // ——— Water System Maintenance (existing + docs 3.1) ———
  daily_potable_water_inspection: type({
    label: 'Daily Potable Water System Inspection',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Daily potable water system inspection',
    description:
      'Complete the daily potable water system walkthrough: check storage levels, reticulation pressure, visible leaks, chlorine residual where applicable, and record readings. Escalate faults immediately.\n\nRef: Water Supply overview — ' +
      `${DOC}/sections/3-infrastructure/3.1-water-supply/`,
    location: 'Water treatment / storage / reticulation',
    ideal_outcome: 'System confirmed operational; readings logged; any faults raised.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1-water-supply/`,
  }),
  daily_chlorination_inspection: type({
    label: 'Daily Chlorination Dosing System Inspection',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Daily chlorination dosing system inspection',
    description:
      'Inspect the Prominent Beta 4 dosing pump, chemical level, injection point, and pulse-meter trigger. Confirm dosing is occurring with flow and note residual checks.\n\nRef: Chlorine dosing — ' +
      `${DOC}/sections/3-infrastructure/3.1.3-chlorine-dosing-system/`,
    location: 'Chlorine dosing / green shed control hub',
    ideal_outcome: 'Dosing system running correctly; chemical stock adequate; residuals in range.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1.3-chlorine-dosing-system/`,
  }),
  daily_bore_pump_inspection: type({
    label: 'Daily Bore Farm and Pumps Inspection',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Daily bore farm and pumps inspection',
    description:
      'Inspect Bore 1 (RN43075) and Bore 2 (RN32812) equipment, Kelco F60 controller, bladder tank, alarms, and mode switches in the green shed. Note abnormal noise, pressure, or fault indicators.\n\nRef: Bore pump — ' +
      `${DOC}/sections/3-infrastructure/3.1.1-bore-pump/`,
    location: 'Bore farm / green shed',
    ideal_outcome: 'Bore supply healthy; controller and alarms normal; issues logged.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1.1-bore-pump/`,
  }),
  storage_tank_inspection: type({
    label: 'Storage Tank Inspection',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Storage tank inspection',
    description:
      'Inspect steel Rhino RT-310 and poly tanks 2–7 for levels, integrity, overflows, valves, and access security. Record approximate stored volume.\n\nRef: Storage tanks — ' +
      `${DOC}/sections/3-infrastructure/3.1.2-storage-tanks/`,
    location: 'Hill-top / tank compound',
    ideal_outcome: 'Tanks secure and intact; levels recorded; defects reported.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1.2-storage-tanks/`,
  }),
  reticulation_valve_check: type({
    label: 'Reticulation / Isolation Valve Check',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Reticulation and isolation valve check',
    description:
      'Check central distribution box valves and known isolation points (units segments, western easement, flush-out valves). Confirm operable position and no leaks.\n\nRef: Reticulation — ' +
      `${DOC}/sections/3-infrastructure/3.1.4-reticulation/`,
    location: 'Central distribution box / reticulation mains',
    ideal_outcome: 'Isolation valves operable; no uncontrolled leaks; positions recorded.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1.4-reticulation/`,
  }),
  water_monitoring_check: type({
    label: 'Water Monitoring System Check',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Water monitoring system check',
    description:
      'Verify mechanical/ultrasonic meters and remote flow panel readings for bore and potable supply. Compare panel totals to field indicators.\n\nRef: Flow monitoring — ' +
      `${DOC}/sections/3-infrastructure/3.4-flow-monitoring/`,
    location: 'Green shed meters / Lake Bennett Panel',
    ideal_outcome: 'Meters polling; panel data sensible; anomalies investigated.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.4-flow-monitoring/`,
  }),
  bore_performance_monitoring: type({
    label: 'Bore Performance Monitoring',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Bore performance monitoring',
    description:
      'Record bore output (~100 L/min design), runtime, and any performance decline. Note which bore is in service.\n\nRef: Bore pump — ' +
      `${DOC}/sections/3-infrastructure/3.1.1-bore-pump/`,
    location: 'Bore farm',
    ideal_outcome: 'Performance logged; decline or faults escalated.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1.1-bore-pump/`,
  }),
  water_system_fault_response: type({
    label: 'Water System Fault Response',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Water system fault response',
    description:
      'Respond to a potable water fault (pump, dosing, leak, low pressure, or alarm). Isolate safely, restore supply if possible, and document cause and actions.\n\nRef: Water supply — ' +
      `${DOC}/sections/3-infrastructure/3.1-water-supply/`,
    location: 'As indicated by alarm / callout',
    ideal_outcome: 'Fault contained; supply restored or contingency in place; report completed.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1-water-supply/`,
  }),
  water_conservation_action: type({
    label: 'Water Conservation Action',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Water conservation action',
    description:
      'Carry out water conservation measures (leak follow-up, restriction messaging, or usage review) per estate guidance.\n\nRef: Water conservation — ' +
      `${DOC}/sections/3-infrastructure/3.1.5-water-conservation/`,
    location: 'Estate-wide',
    ideal_outcome: 'Conservation measures applied and recorded.',
    priority: 'low',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1.5-water-conservation/`,
  }),
  bore_water_sampling: type({
    label: 'Bore Water Sampling (6-monthly)',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Bore water sampling (6-monthly)',
    description:
      'Collect bore water samples per documented sampling procedure with chain-of-custody. Arrange lab analysis against drinking-water guideline parameters.\n\nRef: Water supply / sampling manuals on docs site.',
    location: 'Bore sample points',
    ideal_outcome: 'Samples submitted; CoC complete; results filed when returned.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1-water-supply/`,
  }),
  storage_tank_sampling: type({
    label: 'Storage Tank Water Sampling (6-monthly)',
    group: 'Water Supply',
    category: 'Water Supply',
    title: 'Storage tank water sampling (6-monthly)',
    description:
      'Sample storage tanks per procedure for microbiological/chemical checks. Document tank ID, time, and handling.\n\nRef: Storage tanks — ' +
      `${DOC}/sections/3-infrastructure/3.1.2-storage-tanks/`,
    location: 'Storage tank compound',
    ideal_outcome: 'Samples submitted and documented.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.1.2-storage-tanks/`,
  }),
  contractor_coordination: type({
    label: 'Contractor Coordination',
    group: 'Water Supply',
    category: 'Administration',
    title: 'Contractor coordination — water/infrastructure',
    description:
      'Coordinate contractor attendance for water or related infrastructure works. Confirm scope, access, permits, and site induction.',
    location: 'As required for works',
    ideal_outcome: 'Contractor briefed, scheduled, and supervised as needed.',
  }),
  contracted_works_supervision: type({
    label: 'On-site Contracted Works Supervision',
    group: 'Water Supply',
    category: 'Administration',
    title: 'On-site contracted works supervision',
    description:
      'Supervise contracted works on site. Verify workmanship, safety, and completion against the agreed scope.',
    location: 'Works site',
    ideal_outcome: 'Works completed satisfactorily; defects noted; handover recorded.',
  }),

  // ——— Wastewater / OWMS (docs 3.2) ———
  daily_wastewater_inspection: type({
    label: 'Daily Wastewater System Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'Daily wastewater collection system inspection',
    description:
      'Inspect collection network pump stations (SSP1–SSP5) for alarms, levels, odours, and abnormal operation across Zones 1–3.\n\nRef: Sewerage overview — ' +
      `${DOC}/sections/3-infrastructure/3.2-sewerage-and-wastewater/`,
    location: 'SSP1 / SSP2 / SSP3 / SSP4 / SSP5 as scheduled',
    ideal_outcome: 'Stations healthy; faults escalated; daily log complete.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2-sewerage-and-wastewater/`,
  }),
  daily_recycling_plant_inspection: type({
    label: 'Daily Wastewater Recycling Plant Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'Daily WWTP / recycling plant inspection',
    description:
      'Inspect AdvanTex WWTP, primary tanks, disinfection/polishing, and recycled water storage. Note flows, alarms, and visual condition.\n\nRef: WWTP overview — ' +
      `${DOC}/sections/3-infrastructure/3.2.10-wastewater-treatment-plant-overview/`,
    location: 'Wastewater treatment plant',
    ideal_outcome: 'Plant treating normally; recycled water quality pathway intact; issues logged.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.10-wastewater-treatment-plant-overview/`,
  }),
  ssp1_inspection: type({
    label: 'SSP1 Main Septic / Lift Well Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'SSP1 main septic and lift well inspection',
    description:
      'Inspect SSP1 (SPS1, Zone 1 bungalows 17–59): septic, lift well, pumps, floats, and alarms.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.1-ssp1-main-septic-and-lift-well/`,
    location: 'SSP1 / SPS1',
    ideal_outcome: 'SSP1 operational; defects recorded.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.1-ssp1-main-septic-and-lift-well/`,
  }),
  ssp2_inspection: type({
    label: 'SSP2 STEP Tank Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'SSP2 STEP tank inspection',
    description:
      'Inspect SSP2 STEP tank (Zone 2 bungalows 1–16) pumps, levels, and alarms.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.2-ssp2-infrastructure/`,
    location: 'SSP2 / SPS2',
    ideal_outcome: 'SSP2 operational; defects recorded.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.2-ssp2-infrastructure/`,
  }),
  ssp3_inspection: type({
    label: 'SSP3 Transfer Pump Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'SSP3 transfer pump station inspection',
    description:
      'Inspect SSP3 transfer pump station (Zone 3) and rising main path toward SPS1.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.3-ssp3-transfer-pump-station/`,
    location: 'SSP3 / SPS3',
    ideal_outcome: 'Transfer pumps healthy; rising main issues noted.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.3-ssp3-transfer-pump-station/`,
  }),
  ssp4_inspection: type({
    label: 'SSP4 Everhard Septic Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'SSP4 Everhard septic inspection',
    description:
      'Inspect SSP4 Everhard septic unit serving Zone 3 properties.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.4-ssp4-everhard-septic/`,
    location: 'SSP4',
    ideal_outcome: 'SSP4 serviceable; maintenance needs logged.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.4-ssp4-everhard-septic/`,
  }),
  ssp5_inspection: type({
    label: 'SSP5 AquaTreat Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'SSP5 AquaTreat inspection',
    description:
      'Inspect SSP5 modified AquaTreat unit.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.5-ssp5-modified-aquatreat/`,
    location: 'SSP5',
    ideal_outcome: 'SSP5 serviceable; maintenance needs logged.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.5-ssp5-modified-aquatreat/`,
  }),
  gravity_mains_inspection: type({
    label: 'Sewer Gravity Mains Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'Sewer gravity mains inspection',
    description:
      'Inspect accessible gravity sewer mains/manholes for blockages, surcharging, or damage.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.6-sewer-gravity-mains/`,
    location: 'Gravity sewer network',
    ideal_outcome: 'Mains clear/flowing; defects mapped.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.6-sewer-gravity-mains/`,
  }),
  rising_mains_inspection: type({
    label: 'Rising Sewer Mains Inspection',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'Rising sewer mains inspection',
    description:
      'Inspect rising sewer mains (including lake crossing route) for leaks, pressure issues, or air-valve problems.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.7-rising-sewer-mains/`,
    location: 'Rising mains corridor',
    ideal_outcome: 'Rising mains intact; issues escalated.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.7-rising-sewer-mains/`,
  }),
  wwtp_primary_treatment_check: type({
    label: 'WWTP Primary Treatment Check',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'WWTP primary treatment tanks check',
    description:
      'Check primary treatment tanks for levels, scum, sludge indicators, and inlet condition.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.11-primary-treatment-tanks/`,
    location: 'WWTP primary tanks',
    ideal_outcome: 'Primary stage stable; desludge needs noted.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.11-primary-treatment-tanks/`,
  }),
  advantex_secondary_check: type({
    label: 'AdvanTex Secondary Treatment Check',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'AdvanTex secondary treatment check',
    description:
      'Inspect AdvanTex pods/filters, recirculation, and secondary treatment performance indicators.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.12-advantex-secondary-treatment/`,
    location: 'WWTP AdvanTex',
    ideal_outcome: 'Secondary treatment operating; media/filter issues logged.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.12-advantex-secondary-treatment/`,
  }),
  disinfection_polishing_check: type({
    label: 'Disinfection & Final Polishing Check',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'Disinfection and final polishing check',
    description:
      'Verify disinfection/polishing stage supporting Class A recycled water pathway.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.13-disinfection-and-final-polishing/`,
    location: 'WWTP disinfection / polishing',
    ideal_outcome: 'Disinfection stage healthy; residuals/process notes recorded.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.13-disinfection-and-final-polishing/`,
  }),
  recycled_water_storage_check: type({
    label: 'Recycled Water Storage Check',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'Recycled water storage check',
    description:
      'Inspect four ~27 kL recycled water tanks (108 kL total): levels, integrity, valves, and odours.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.14-recycled-water-storage/`,
    location: 'Recycled water storage',
    ideal_outcome: 'Storage intact; usable volume recorded.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.14-recycled-water-storage/`,
  }),
  effluent_disposal_area_check: type({
    label: 'Effluent Disposal / LAA Check',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'Effluent disposal / land application area check',
    description:
      'Inspect subsurface dripline irrigation zones (LAA) for ponding, damage, wet spots, or irrigation faults.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.8-effluent-disposal-area/`,
    location: 'Land application areas',
    ideal_outcome: 'LAA disposing evenly; no public-health hazards; defects fixed or raised.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.8-effluent-disposal-area/`,
  }),
  tcom_control_check: type({
    label: 'T-COM Control / HMI Check',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'T-COM control interface / HMI check',
    description:
      'Review T-COM control interface, web interface, viewer, and/or local HMI for alarms and process status.\n\nRefs: T-COM pages under sewerage section on docs site.',
    location: 'WWTP control / T-COM',
    ideal_outcome: 'Controls reachable; active alarms acknowledged/actioned.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.16-tcom-control-interface/`,
  }),
  owms_contingency_action: type({
    label: 'OWMS Contingency / Emergency Response',
    group: 'Sewerage & Wastewater',
    category: 'Sewerage',
    title: 'OWMS contingency / emergency response',
    description:
      'Execute OWMS contingency actions for overflow risk, plant failure, or disposal constraints per O&M guidance.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.2.15-owms-operations-maintenance-contingency/`,
    location: 'WWTP / affected network',
    ideal_outcome: 'Risk contained; contingency logged; management notified.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.2.15-owms-operations-maintenance-contingency/`,
  }),

  // ——— Power (docs 3.3) ———
  grid_power_inspection: type({
    label: 'Grid Power Inspection',
    group: 'Power Supply',
    category: 'Power',
    title: 'Grid power supply inspection',
    description:
      'Inspect estate grid power infrastructure for outages, damaged poles/lines (visible), switchgear issues, and site safety.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.3.1-grid/`,
    location: 'Estate electrical infrastructure',
    ideal_outcome: 'Supply status confirmed; hazards isolated/reported.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.3.1-grid/`,
  }),
  backup_power_test: type({
    label: 'Backup Power Test / Inspection',
    group: 'Power Supply',
    category: 'Power',
    title: 'Backup power test / inspection',
    description:
      'Inspect and/or test backup power arrangements. Confirm readiness for outages.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.3.2-backup-power/`,
    location: 'Backup power plant',
    ideal_outcome: 'Backup power ready or defects scheduled.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.3.2-backup-power/`,
  }),
  power_outage_response: type({
    label: 'Power Outage Response',
    group: 'Power Supply',
    category: 'Power',
    title: 'Power outage response',
    description:
      'Respond to estate power outage: confirm extent, start contingency for critical water/wastewater assets, and communicate status.\n\nRef: Power supply — ' +
      `${DOC}/sections/3-infrastructure/3.3-power-supply/`,
    location: 'Estate-wide / critical plant',
    ideal_outcome: 'Critical services protected; outage timeline recorded.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.3-power-supply/`,
  }),

  // ——— Flow monitoring (docs 3.4) ———
  flow_panel_monitoring: type({
    label: 'Flow Panel Monitoring Review',
    group: 'Flow Monitoring',
    category: 'Flow Monitoring',
    title: 'Lake Bennett Panel monitoring review',
    description:
      'Review bore, soak drain, and potable ultrasonic meter totals/daily usage on the Lake Bennett Panel. Note unusual spikes.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.4.1-using-the-panel/`,
    location: 'flow.panel.lakebennettestate.com',
    ideal_outcome: 'Usage reviewed; anomalies investigated.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.4-flow-monitoring/`,
  }),
  flow_alarm_response: type({
    label: 'Flow Alarm Response',
    group: 'Flow Monitoring',
    category: 'Flow Monitoring',
    title: 'Flow monitoring alarm response',
    description:
      'Investigate and clear/action a Lake Bennett Panel flow alarm. Confirm field conditions and update alarm history notes.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.4.3-alarms/`,
    location: 'Panel + related meter site',
    ideal_outcome: 'Alarm cause identified; corrective action taken or scheduled.',
    priority: 'high',
    docsUrl: `${DOC}/sections/3-infrastructure/3.4.3-alarms/`,
  }),
  flow_diagnostics_check: type({
    label: 'Flow Diagnostics / Meter Polling Check',
    group: 'Flow Monitoring',
    category: 'Flow Monitoring',
    title: 'Flow diagnostics / meter polling check',
    description:
      'Use Diagnostics to confirm meters are polling via Modbus/gateways. Escalate offline devices.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.4.5-diagnostics/`,
    location: 'Panel diagnostics / field gateways',
    ideal_outcome: 'All expected meters online; offline devices ticketed.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.4.5-diagnostics/`,
  }),
  flow_field_hardware_service: type({
    label: 'Flow Field Hardware Service',
    group: 'Flow Monitoring',
    category: 'Flow Monitoring',
    title: 'Flow field hardware service',
    description:
      'Service ultrasonic meters, RS485 wiring, or RS485-to-Ethernet gateways supporting the flow panel.\n\nRef: ' +
      `${DOC}/sections/3-infrastructure/3.4.6-field-hardware/`,
    location: 'Meter / gateway locations',
    ideal_outcome: 'Hardware restored; polling confirmed.',
    docsUrl: `${DOC}/sections/3-infrastructure/3.4.6-field-hardware/`,
  }),

  // ——— Roads (docs 4) ———
  road_inspection: type({
    label: 'Internal Road Inspection',
    group: 'Roads',
    category: 'Roads',
    title: 'Internal road inspection',
    description:
      'Inspect Eastside Road (~400 m) and Northshore Road (~250 m) for potholes, edge break, drainage issues, and safety hazards.\n\nRef: ' +
      `${DOC}/sections/4-roads/4.1-internal-roads/`,
    location: 'Eastside Road / Northshore Road',
    ideal_outcome: 'Defects listed with priority for patching or contractor works.',
    docsUrl: `${DOC}/sections/4-roads/4.1-internal-roads/`,
  }),
  road_patching: type({
    label: 'Road Patching (EZ Street)',
    group: 'Roads',
    category: 'Roads',
    title: 'Road patching with EZ Street',
    description:
      'Patch bitumen defects using EZ Street (Fulton Hogan) per product guidance. Record locations and quantity used.\n\nRef: ' +
      `${DOC}/sections/4-roads/4.1-internal-roads/`,
    location: 'Named road defect locations',
    ideal_outcome: 'Defects patched safely; materials usage logged.',
    docsUrl: `${DOC}/sections/4-roads/4.1-internal-roads/`,
  }),
  driveway_maintenance: type({
    label: 'Driveway Maintenance',
    group: 'Roads',
    category: 'Roads',
    title: 'Driveway maintenance',
    description:
      'Inspect/repair estate or common driveway surfaces affecting access.\n\nRef: ' +
      `${DOC}/sections/4-roads/4.2-driveways/`,
    location: 'Specified driveway',
    ideal_outcome: 'Access restored; hazards removed.',
    docsUrl: `${DOC}/sections/4-roads/4.2-driveways/`,
  }),
  traffic_safety_check: type({
    label: 'Traffic Safety Check',
    group: 'Roads',
    category: 'Roads',
    title: 'Traffic safety check',
    description:
      'Check signage, sight lines, and traffic safety controls on estate roads.\n\nRef: ' +
      `${DOC}/sections/4-roads/4.3-traffic-safety/`,
    location: 'Internal road network',
    ideal_outcome: 'Safety issues corrected or escalated.',
    docsUrl: `${DOC}/sections/4-roads/4.3-traffic-safety/`,
  }),
  emergency_access_check: type({
    label: 'Emergency Access Check',
    group: 'Roads',
    category: 'Roads',
    title: 'Emergency access check',
    description:
      'Verify emergency vehicle access routes are clear and usable.\n\nRef: ' +
      `${DOC}/sections/4-roads/4.4-emergency-access/`,
    location: 'Emergency access routes',
    ideal_outcome: 'Access clear; obstructions removed.',
    priority: 'high',
    docsUrl: `${DOC}/sections/4-roads/4.4-emergency-access/`,
  }),
  street_light_maintenance: type({
    label: 'Street Light Maintenance',
    group: 'Roads',
    category: 'Roads',
    title: 'Street light maintenance',
    description:
      'Inspect/repair street lighting faults affecting roads or common areas.\n\nRef: ' +
      `${DOC}/sections/4-roads/4.5-street-lights/`,
    location: 'Named light / road section',
    ideal_outcome: 'Lights operating or works ordered.',
    docsUrl: `${DOC}/sections/4-roads/4.5-street-lights/`,
  }),

  // ——— Grounds (existing) ———
  absorption_area_mowing: type({
    label: 'Absorption Area Lawn Mowing',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Absorption area lawn mowing',
    description:
      'Mow absorption / land application related lawn areas. Avoid damaging dripline and keep vegetation at maintenance height.',
    location: 'Absorption / LAA grassed areas',
    ideal_outcome: 'Area mown; irrigation assets undamaged.',
  }),
  treatment_plant_mowing: type({
    label: 'Water Treatment Plant Area Mowing',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Water treatment plant area mowing',
    description: 'Mow and tidy vegetation around the water treatment / WWTP compound for access and fire risk reduction.',
    location: 'Treatment plant compound',
    ideal_outcome: 'Compound accessible and tidy.',
  }),
  northern_slope_mowing: type({
    label: 'Northern Slope Eastern Side Mowing',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Northern slope eastern side mowing',
    description: 'Mow northern slope eastern side to maintain access and amenity.',
    location: 'Northern slope — eastern side',
    ideal_outcome: 'Slope mown safely; erosion issues noted.',
  }),
  powerline_reserve_mowing: type({
    label: 'Powerline Reserve Mowing',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Powerline reserve mowing',
    description: 'Mow powerline reserve corridors while observing electrical safety clearances.',
    location: 'Powerline reserve',
    ideal_outcome: 'Reserve maintained; hazards reported.',
    priority: 'high',
  }),
  green_waste_collection: type({
    label: 'Weekly Green Waste Collection',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Weekly green waste collection',
    description: 'Collect green waste from designated estate points and consolidate for mulching/redistribution.',
    location: 'Green waste collection points',
    ideal_outcome: 'Collection complete; stockpile orderly.',
  }),
  green_waste_mulching: type({
    label: 'Green Waste Mulching (bi-monthly)',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Green waste mulching (bi-monthly)',
    description: 'Mulch consolidated green waste and prepare material for redistribution.',
    location: 'Green waste processing area',
    ideal_outcome: 'Waste mulched; usable product staged.',
  }),
  green_waste_redistribution: type({
    label: 'Green Waste Redistribution',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Green waste redistribution',
    description: 'Redistribute mulch/green waste product to approved garden or reserve areas.',
    location: 'As directed',
    ideal_outcome: 'Material redistributed without blocking drains/access.',
  }),
  spillway_park_maintenance: type({
    label: 'Spillway Park Garden Maintenance',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Spillway park garden maintenance',
    description: 'Maintain Spillway Park gardens: weeding, pruning, litter, and presentation.',
    location: 'Spillway Park',
    ideal_outcome: 'Park presented and safe.',
  }),
  eastern_reserve_maintenance: type({
    label: 'Eastern Reserve Garden Maintenance',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Eastern reserve garden maintenance',
    description: 'Maintain eastern reserve plantings and amenity areas.',
    location: 'Eastern reserve',
    ideal_outcome: 'Reserve tidy; invasive species noted.',
  }),
  eastern_hill_slope_maintenance: type({
    label: 'Eastern Hill Slope Garden Maintenance',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Eastern hill slope garden maintenance',
    description: 'Maintain eastern hill slope gardens and control erosion-prone bare patches.',
    location: 'Eastern hill slope',
    ideal_outcome: 'Slope vegetation maintained; erosion noted.',
  }),
  northern_slope_garden_installation: type({
    label: 'Northern Slope Garden Installation',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Northern slope garden installation',
    description: 'Install new garden plantings on the northern slope per plan.',
    location: 'Northern slope',
    ideal_outcome: 'Planting complete and watered in.',
  }),
  northern_slope_garden_maintenance: type({
    label: 'Northern Slope Garden Maintenance',
    group: 'Grounds & Gardens',
    category: 'Grounds',
    title: 'Northern slope garden maintenance',
    description: 'Ongoing maintenance of northern slope gardens.',
    location: 'Northern slope',
    ideal_outcome: 'Gardens healthy and weeded.',
  }),

  // ——— Weeds ———
  routine_weed_control: type({
    label: 'Routine Weed Control - Gardens',
    group: 'Weed Management',
    category: 'Grounds',
    title: 'Routine weed control — gardens',
    description: 'Undertake routine weed control in maintained garden beds using approved methods.',
    location: 'Estate gardens',
    ideal_outcome: 'Weeds reduced; desirable plants protected.',
  }),
  invasive_tree_management: type({
    label: 'Invasive Tree Species Management',
    group: 'Weed Management',
    category: 'Grounds',
    title: 'Invasive tree species management',
    description: 'Identify and manage invasive tree species (cut/treat/remove as approved).',
    location: 'Affected reserve / slope',
    ideal_outcome: 'Target trees treated; regrowth plan noted.',
  }),
  invasive_grass_management: type({
    label: 'Invasive Grass Species Management',
    group: 'Weed Management',
    category: 'Grounds',
    title: 'Invasive grass species management',
    description: 'Control invasive grasses in reserves, slopes, and garden edges.',
    location: 'Affected areas',
    ideal_outcome: 'Infestation reduced; follow-up scheduled if needed.',
  }),
  hill_area_weed_control: type({
    label: 'Hill Area Weed Control',
    group: 'Weed Management',
    category: 'Grounds',
    title: 'Hill area weed control',
    description: 'Weed control focused on hill areas with access and erosion caution.',
    location: 'Hill areas',
    ideal_outcome: 'Weeds controlled; soil disturbance minimised.',
  }),
  reserve_area_weed_control: type({
    label: 'Reserve Area Weed Control',
    group: 'Weed Management',
    category: 'Grounds',
    title: 'Reserve area weed control',
    description: 'Weed control in estate reserve areas.',
    location: 'Reserves',
    ideal_outcome: 'Reserves improved; native vegetation protected.',
  }),
  seasonal_growth_management: type({
    label: 'Seasonal Growth Management - Wet Season Transition',
    group: 'Weed Management',
    category: 'Grounds',
    title: 'Seasonal growth management — wet season transition',
    description: 'Manage rapid wet-season growth: slashing, weed burst control, and drain clearance prep.',
    location: 'Estate-wide priority areas',
    ideal_outcome: 'Growth managed ahead of peak wet conditions.',
    priority: 'high',
  }),

  // ——— Stormwater ———
  roadside_drain_cleaning: type({
    label: 'Roadside Stormwater Drain Cleaning',
    group: 'Stormwater',
    category: 'Stormwater',
    title: 'Roadside stormwater drain cleaning',
    description:
      'Clear roadside table drains and culvert inlets of silt, vegetation, and debris to restore drainage.\n\nRelated: road drainage notes in ' +
      `${DOC}/sections/4-roads/4.1-internal-roads/`,
    location: 'Roadside drains',
    ideal_outcome: 'Drains free-flowing; flood risk reduced.',
    docsUrl: `${DOC}/sections/4-roads/4.1-internal-roads/`,
  }),
  hillside_drain_cleaning: type({
    label: 'Hillside Stormwater Drain Cleaning',
    group: 'Stormwater',
    category: 'Stormwater',
    title: 'Hillside stormwater drain cleaning',
    description: 'Clean hillside stormwater drains and check for scour after rainfall.',
    location: 'Hillside drains',
    ideal_outcome: 'Drains clear; scour repaired or flagged.',
  }),
  drain_debris_removal: type({
    label: 'Stormwater Drain Debris Removal',
    group: 'Stormwater',
    category: 'Stormwater',
    title: 'Stormwater drain debris removal',
    description: 'Remove debris blockages from stormwater drains after storm events.',
    location: 'Blocked drain locations',
    ideal_outcome: 'Blockages cleared; photos/notes attached if significant.',
    priority: 'high',
  }),
  drain_preventative_maintenance: type({
    label: 'Stormwater Drain Preventative Maintenance',
    group: 'Stormwater',
    category: 'Stormwater',
    title: 'Stormwater drain preventative maintenance',
    description: 'Scheduled preventative maintenance of stormwater assets before wet season.',
    location: 'Priority drain network',
    ideal_outcome: 'Network prepared for wet season flows.',
  }),
  erosion_control_maintenance: type({
    label: 'Erosion Control Maintenance',
    group: 'Stormwater',
    category: 'Stormwater',
    title: 'Erosion control maintenance',
    description: 'Maintain erosion control measures (rock, vegetation, bunds) on slopes and drain outlets.',
    location: 'Erosion sites',
    ideal_outcome: 'Erosion stabilised or further works scheduled.',
  }),

  // ——— Planned works / general ———
  planned_upgrade: type({
    label: 'Planned Upgrade / Outage Works',
    group: 'Planned Works',
    category: 'Administration',
    title: 'Planned upgrade / outage works',
    description:
      'Plan and execute scheduled upgrades or service outages with resident communication.\n\nRef: ' +
      `${DOC}/sections/5-planned-works/5-upgrades-and-outages/`,
    location: 'Affected infrastructure',
    ideal_outcome: 'Works completed; outage window met; stakeholders informed.',
    docsUrl: `${DOC}/sections/5-planned-works/5-upgrades-and-outages/`,
  }),
  general_maintenance: type({
    label: 'General Estate Maintenance',
    group: 'General',
    category: 'General',
    title: 'General estate maintenance',
    description: 'General maintenance task not covered by a more specific type. Describe scope clearly before starting.',
    location: 'As specified',
    ideal_outcome: 'Work complete and noted for future specific task typing if recurring.',
    priority: 'low',
  }),
  resident_request: type({
    label: 'Resident / Owner Request',
    group: 'General',
    category: 'General',
    title: 'Resident / owner request',
    description: 'Action a request from a resident or owner. Record requester, lot/unit if known, and resolution.',
    location: 'Lot / unit as advised',
    ideal_outcome: 'Request resolved or formally escalated with response given.',
  }),
  inspection_other: type({
    label: 'Other Infrastructure Inspection',
    group: 'General',
    category: 'General',
    title: 'Other infrastructure inspection',
    description:
      'Inspection of estate infrastructure not listed under a dedicated type. Prefer a specific type when possible — see ' +
      `${DOC}/`,
    location: 'As specified',
    ideal_outcome: 'Inspection complete with findings logged.',
    docsUrl: `${DOC}/`,
  }),
};

/** Group order for the select dropdown */
export const TASK_TYPE_GROUP_ORDER = [
  'Water Supply',
  'Sewerage & Wastewater',
  'Power Supply',
  'Flow Monitoring',
  'Roads',
  'Grounds & Gardens',
  'Weed Management',
  'Stormwater',
  'Planned Works',
  'General',
];

export function getTaskType(id) {
  return TASK_TYPES[id] || null;
}

/** Human-readable label for a stored task_type id (falls back to the raw id). */
export function formatTaskTypeLabel(id) {
  if (!id) return '';
  return getTaskType(id)?.label || String(id).replace(/_/g, ' ');
}

export function getTaskTypesGrouped() {
  const grouped = {};
  for (const group of TASK_TYPE_GROUP_ORDER) {
    grouped[group] = [];
  }
  for (const [id, def] of Object.entries(TASK_TYPES)) {
    const group = def.group || 'General';
    if (!grouped[group]) grouped[group] = [];
    grouped[group].push({ id, ...def });
  }
  for (const group of Object.keys(grouped)) {
    grouped[group].sort((a, b) => a.label.localeCompare(b.label));
  }
  return grouped;
}

/** Defaults applied to the task form when a type is selected */
export function getDefaultsForTaskType(id) {
  const def = getTaskType(id);
  if (!def) return null;
  return {
    title: def.title,
    description: def.description,
    location: def.location || '',
    ideal_outcome: def.ideal_outcome || '',
    category: def.category || def.group || '',
    priority: def.priority || 'medium',
    task_type: id,
  };
}
